import { DimensionConfig, MetricsConfig, PERFORMANCE_COLORS, PerformanceColorConfig } from './consts';
import { buildFilter, buildMetricParams } from '../utils/reportQueryUtils';

const periodKey = (key, period) => `${key}__${period}`;

const getColumnKey = metricKey => {
    const config = MetricsConfig[metricKey];
    if (!config) return null;

    const { apiName, apiType, period } = config;
    return apiType === 'cohort' ? periodKey(apiName, period) : apiName;
};

export const buildQueryParams = (dimensions, { filters, firstPeriod, secondPeriod }) => {
    const apiFilters = filters
        .filter(f => f.values?.length)
        .map(filter =>
            buildFilter(
                DimensionConfig[filter.type].apiName,
                filter.values.map(value => value.value)
            )
        );

    return {
        dimensions: dimensions.map(d => DimensionConfig[d.value].apiName),
        ...buildMetricParams(Object.values(MetricsConfig)),
        filters: apiFilters,
        compare: true,
        start_date: firstPeriod.start,
        end_date: firstPeriod.end,
        start_date_2: secondPeriod.start,
        end_date_2: secondPeriod.end,
        time_breakdown: ['all'],
    };
};

export const buildQueryKey = ({ dimensions, filters, firstPeriod, secondPeriod }) =>
    JSON.stringify({
        dimensions: dimensions.map(d => d.value).sort(),
        filters: filters
            .map(f => ({ type: f.type, values: (f.values || []).map(v => v.value).sort() }))
            .sort((a, b) => a.type.localeCompare(b.type)),
        firstPeriod,
        secondPeriod,
    });

const getColorForChange = pct => {
    if (!pct || !Number.isFinite(pct)) return PERFORMANCE_COLORS.NEUTRAL;

    const match = Object.values(PerformanceColorConfig).find(({ min, max }) => min != null && pct >= min && pct < max);
    return match?.color ?? PERFORMANCE_COLORS.NEUTRAL;
};

const PERIOD1 = 'period1';
const PERIOD2 = 'period2';

const getValue = (row, key) => {
    const num = Number(row[key]);
    return Number.isNaN(num) ? 0 : num;
};

const computeColor = (colorP1, colorP2) => {
    if (colorP1 === 0 && colorP2 === 0) return PERFORMANCE_COLORS.NO_DATA;
    if (colorP2 === 0) return colorP1 > 0 ? PERFORMANCE_COLORS.EXCELLENT : PERFORMANCE_COLORS.POOR;
    return getColorForChange(((colorP1 - colorP2) / Math.abs(colorP2)) * 100);
};

const buildDimensionNodes = (rows, dimension, colorKey, sizeKey, ringIndex) => {
    const dimApiName = DimensionConfig[dimension.value].apiName;
    const sizeP1Key = periodKey(sizeKey, PERIOD1);
    const colorP1Key = periodKey(colorKey, PERIOD1);
    const colorP2Key = periodKey(colorKey, PERIOD2);

    return rows
        .filter(row => getValue(row, sizeP1Key))
        .map(row => {
            const name = row[dimApiName] != null ? String(row[dimApiName]) : 'Unknown';
            const colorP1 = getValue(row, colorP1Key);
            const colorP2 = getValue(row, colorP2Key);
            return {
                id: `ring${ringIndex}|${dimApiName}:${name}`,
                name,
                value: getValue(row, sizeP1Key),
                color: computeColor(colorP1, colorP2),
                ringIndex,
            };
        });
};

export const transformToSunburstData = (dimensionResults, { colorMetric, sizeMetric }) => {
    const colorKey = getColumnKey(colorMetric.value);
    const sizeKey = getColumnKey(sizeMetric.value);
    if (!colorKey || !sizeKey) return [];

    return dimensionResults.flatMap(({ dimension, rows }, index) =>
        buildDimensionNodes(rows, dimension, colorKey, sizeKey, index)
    );
};
